import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useNotification } from '../../../../context/NotificationContext';
import GoogleLoginButton from '../../../../components/GoogleLoginButton';
import AppleLoginButton from '../../../../components/AppleLoginButton';


const DoctorRegister = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    specialization: '',
    license_number: '',
    experience: '',
    bio: '',
    address: '',
    education: '',
    terms: false
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.specialization.trim()) newErrors.specialization = 'Specialization is required';
    if (!formData.license_number.trim()) newErrors.license_number = 'License number is required';
    if (!formData.terms) newErrors.terms = 'You must accept the Terms and Conditions';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Password match
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Phone validation (Indian format)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid Indian phone number';
    }

    // Experience validation
    if (formData.experience && (formData.experience < 0 || formData.experience > 50)) {
      newErrors.experience = 'Invalid experience years';
    }

    // License number validation (basic format)
    if (formData.license_number && formData.license_number.length < 5) {
      newErrors.license_number = 'Invalid license number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the validation errors');
      return;
    }

    setLoading(true);

    try {
      const result = await register('doctor', {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
        specialization: formData.specialization.trim(),
        license_number: formData.license_number.trim(),
        experience: parseInt(formData.experience) || 0,
        bio: formData.bio.trim(),
        address: formData.address.trim(),
        education: formData.education.trim(),
        role: 'doctor'
      });

      if (result.success) {
        showSuccess('Doctor registration submitted! Please select a subscription plan to continue.');
        localStorage.setItem('temp_userId', result.user._id);
        navigate(`/subscription?role=doctor&userId=${result.user._id}`);
      } else {
        showError(result.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Network error. Please try again.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gold mb-2">Dr. Vivara</h1>
          <p className="text-light">Doctor Registration</p>
          <p className="text-gray-400 text-sm mt-2">Note: Your account requires admin approval</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.name ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.email ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="Enter your email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.phone ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Specialization *</label>
                <input
                  type="text"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.specialization ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="e.g., Cardiology, Pediatrics"
                />
                {errors.specialization && <p className="text-red-500 text-sm mt-1">{errors.specialization}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Medical License Number *</label>
                <input
                  type="text"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.license_number ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="Enter your medical license number"
                />
                {errors.license_number && <p className="text-red-500 text-sm mt-1">{errors.license_number}</p>}
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Years of Experience</label>
                <input
                  type="number"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className={`w-full bg-gray-700 border ${errors.experience ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                  placeholder="Years of experience"
                  min="0"
                  max="50"
                />
                {errors.experience && <p className="text-red-500 text-sm mt-1">{errors.experience}</p>}
              </div>
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Education</label>
              <input
                type="text"
                name="education"
                value={formData.education}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
                placeholder="e.g., MBBS, MD from XYZ University"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="2"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
                placeholder="Enter your clinic/hospital address"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Professional Bio</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
                placeholder="Brief professional background and expertise"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full bg-gray-700 border ${errors.password ? 'border-red-500' : 'border-gray-600'
                      } rounded-lg py-3 px-4 pr-10 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full bg-gray-700 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                      } rounded-lg py-3 px-4 pr-10 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="flex items-start mt-6">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.checked })}
                  className="focus:ring-gold h-4 w-4 text-gold border-gray-600 rounded bg-gray-700"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium text-gray-300">
                  I agree to the{' '}
                  <Link to="/terms" className="text-gold hover:underline">
                    Terms and Conditions
                  </Link>
                  ,{' '}
                  <Link to="/privacy" className="text-gold hover:underline">
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link to="/support" className="text-gold hover:underline">
                    Support
                  </Link>
                </label>
                {errors.terms && <p className="text-red-500 text-sm mt-1">{errors.terms}</p>}
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4 mt-6">
              <h3 className="text-gold font-semibold mb-2">Important Notice</h3>
              <p className="text-gray-400 text-sm">
                Your account will be reviewed by our admin team. You'll receive an email notification once approved.
                Approval typically takes 1-2 business days.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-dark py-3 rounded-lg font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {loading ? 'Submitting Registration...' : 'Submit Registration'}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-500">Or register with</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <GoogleLoginButton role="doctor" onSuccess={(user) => {
                navigate(`/subscription?role=doctor&userId=${user._id}`);
              }} />
              <AppleLoginButton role="doctor" onSuccess={(user) => navigate('/doctor/subscription')} />
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/auth/doctor/login')}
                className="text-gold hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    </div >
  );
};

export default DoctorRegister;
